import React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ResponsiveImage, {
  ImageParams,
} from 'components/Common/ResponsiveImage'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AutoTooltip, { AutoTooltipContent } from 'components/Common/AutoTooltip'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Pill from 'components/Luxkit/Pill'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import InlineGroup from 'components/Common/InlineGroup'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { connect } from 'react-redux'
import Label from 'components/Luxkit/Label/Label'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PrimaryButtonPaletteProvider, {
  PrimaryButtonPalette,
} from 'contexts/PrimaryButtonPaletteProvider'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import PromotionInclusionList from 'components/Cruises/PromotionInclusions/PromotionInclusionList'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import PromotionInclusionItem from 'components/Cruises/PromotionInclusions/PromotionInclusionItem'

const BadgeContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Cruise = styled.div`
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(220)} 1fr;
  border-radius: ${(props) => props.theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: ${rem(280)} 1fr;
    grid-template-rows: 1fr;
  }
  ${mediaQueryUp.desktop} {
    display: grid;
    grid-template-columns: ${rem(500)} 1fr;
    grid-template-rows: 1fr;
  }
`

const TileMediaContainer = styled.div`
  display: flex;
  position: relative;
  height: ${rem(186)};
  min-width: 280px;
  max-width: 700px;

  ${mediaQueryUp.tablet} {
    max-width: 500px;
    height: 100%;
  }
`

const DetailsCaption = styled(Caption)`
  align-items: center;
  display: flex;

  > * + *::before {
    color: ${({ theme }) => theme.palette.neutral.one};
    content: "·";
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const OperatorLogoImage = styled(ResponsiveImage)`
  width: 100%;
  height: 100%;
`

const LogoContainer = styled.div`
  display: flex;
  gap: ${rem(4)};
  flex-direction: row;
  justify-content: space-between;
  ${mediaQueryUp.desktop} {
    gap: ${rem(16)};
  }

  &:empty {
    display: none;
  }
`
const PriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${mediaQueryUp.desktop} {
    gap: ${rem(16)};
  }
  ${mediaQueryUp.tablet} {
    display: block;
    flex-direction: none;
  }
`

const PriceSection = styled.div`
  display: flex;
  justify-content: end;
  flex: 1;
  align-items: flex-end;

  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
    gap: ${rem(32)};
  }
`

const OperatorLogoArea = styled.div`
  display: none;
  max-width: ${rem(150)};
  align-self: center;

  ${mediaQueryUp.desktop} {
    display: flex;
    justify-content: center;
  }
`

const PriceSummary = styled.div`
  position: relative;
  width: 100%;
`

const PriceSectionButton = styled(TextButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  margin-top: ${rem(12)};
  position: static;
  width: auto;

  ${mediaQueryUp.tablet} {
    width: 100%;
  }
`

const Description = styled.div`
  position: relative;
  padding: ${rem(20)};
  gap: ${rem(12)};
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(24)} ${rem(20)} ${rem(28)};
    gap: ${rem(24)};
    grid-template-columns: 1fr ${rem(172)};
  }
`

const DescriptionBody = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  flex-direction: column;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    flex-direction: column;
    gap: ${rem(24)};
  }
  ${mediaQueryUp.desktop} {
    flex-direction: row;
    gap: ${rem(24)};
  }
`

const OperatorLogoMobile = styled.div`
  display: flex;
  min-width: ${rem(78)};
  max-width: ${rem(90)};
  max-height: ${rem(60)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DescriptionSection = styled(VerticalSpacer)`
  height: 100%;
  overflow: hidden;
  display: block;
  display: flex;
  flex: 3;

  ${mediaQueryUp.tablet} {
    flex: 3;
  }
  ${mediaQueryUp.desktop} {
    flex: 1.7;
  }
`
const DescriptionSpace = styled(VerticalSpacer)`
  width: 100%;
`

const ItineraryTooltip = styled(AutoTooltip)`
  display: none;

  > span {
    text-decoration: underline;

    &:hover + div {
      transition: opacity 0.3s ease !important;
    }
  }

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`
const DepartureDateTooltip = styled(AutoTooltip)`

  > span {
    text-decoration: underline;
    margin-left: ${rem(4)};

    &:hover + div {
      transition: opacity 0.3s ease !important;
    }
  }

  display: inline-block;
`
const DepartureDate = styled.div`
  text-decoration: underline;
  margin-left: ${rem(4)};
  display: inline-block;
`

const DepartureDateWrapper = styled.div`
  display: flex;
  align-items: center;
`

const MEDIA_IMAGE_PARAMS: ImageParams = {
  quality: 'eco',
  aspectRatio: '16:9',
  mobileWidth: '100vw',
  tabletWidth: '100vw',
  desktopWidth: '500px',
  largeDesktopWidth: '500px',
}

const DestinationDescriptionText = styled(MarkdownRender)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
    margin-top: ${rem(12)};
  }
`

interface MappedProps {
  isVelocityEnabled: boolean;
}

interface Props {
  productType: App.ProductType;
  images: App.Image[];
  name: string;
  cruiseLine: App.CruiseLine;
  shipName?: string;
  duration?: number;
  disableDeposits?: boolean;
  departurePortCity?: string;
  returnPortCity?: string;
  itinerary?: App.CruiseItineraryItem[];
  promotionDetails?: App.CruisePromotionDetails | null;
  hasTheSameStartAndEndPort?: boolean;
  cities?: (string | undefined)[];
  placesCount?: number;
  countriesCount?: number;
  earliestDepartureDate?: string;
  laterDates?: string[];
  lowestOverallPriceDetails?: App.CruisePriceDetails;
  primaryPrice?: {
    total: number;
    type: string;
    currencyCode: string;
  };
  secondaryPrice?: {
    total: number;
    type: string;
    currencyCode: string;
  };
  discountPills?: App.CruiseDiscountPillDetails | null;
  pricingSection?: React.ReactNode;
  inclusions?: string[];
  bookmarkButton: React.ReactNode;
  palette: PrimaryButtonPalette;
  evergreenInclusions?: App.EvergreenInclusion[];
  description?: string;
  flashOffer?: boolean;
}

function CruisesOfferTile(props: Props & MappedProps) {
  const {
    isVelocityEnabled,
    productType,
    images,
    cruiseLine,
    duration,
    name,
    shipName,
    disableDeposits,
    promotionDetails,
    hasTheSameStartAndEndPort,
    departurePortCity,
    returnPortCity,
    cities,
    placesCount,
    countriesCount,
    earliestDepartureDate,
    laterDates,
    lowestOverallPriceDetails,
    primaryPrice,
    secondaryPrice,
    discountPills,
    pricingSection,
    evergreenInclusions,
    bookmarkButton,
    palette,
    description,
    inclusions,
    flashOffer,
  } = props
  const isMobileScreen = useIsMobileScreen()

  return (
    <Cruise>
      <PrimaryButtonPaletteProvider palette={palette}>
        <TileMediaContainer>
          <ImageCarousel
            topLeft={
              <BadgeContainer>
                <ProductTypeLabel productType={productType} />
                {isVelocityEnabled && (
                  <PartnershipLabel canEarn canUse variant="icon" />
                )}
              </BadgeContainer>
            }
            images={images}
            imageParams={MEDIA_IMAGE_PARAMS}
            eagerLoadFirstImage={false}
          />
        </TileMediaContainer>
        <Description>
          <DescriptionBody>
            <DescriptionSection gap={8}>
              <DescriptionSpace gap={4}>
                <LabelGroup>
                  {promotionDetails?.isNew && (
                    <Label kind="tertiary" variant="ghost">
                      NEW
                    </Label>
                  )}
                  {!!promotionDetails?.isEndingIn &&
                    promotionDetails.isEndingIn <= 7 && (
                      <Label kind="primary" variant="urgency">
                        OFFER ENDS IN{' '}
                        {pluralizeToString(
                          'day',
                          promotionDetails.isEndingIn,
                        ).toLocaleUpperCase()}
                      </Label>
                  )}
                </LabelGroup>
                <LogoContainer>
                  <VerticalSpacer gap={4}>
                    <Caption variant="large">
                      {hasTheSameStartAndEndPort && returnPortCity && (
                        <span>
                          Starts and ends in <b>{returnPortCity}</b>
                        </span>
                      )}
                      {!hasTheSameStartAndEndPort && returnPortCity && (
                        <span>
                          Starts in <b>{departurePortCity}</b>. Ends in{' '}
                          <b>{returnPortCity}</b>
                        </span>
                      )}
                    </Caption>

                    <Heading variant="heading5" lineClamp={3}>
                      {name}
                    </Heading>
                  </VerticalSpacer>
                  {cruiseLine.imageId && (
                    <OperatorLogoMobile>
                      <OperatorLogoImage
                        id={cruiseLine.imageId}
                        mobileWidth="75px"
                        tabletWidth="150px"
                        quality="eco"
                        alt={cruiseLine.name}
                        role="presentation"
                      />
                    </OperatorLogoMobile>
                  )}
                </LogoContainer>
                <DetailsCaption variant="large">
                  <div>{cruiseLine.name}</div>
                  {shipName && <div>{shipName}</div>}
                </DetailsCaption>

                <DetailsCaption variant="large">
                  {duration && (
                    <div>{pluralizeToString('night', duration)}</div>
                  )}
                  {!!placesCount && (
                    <div>{pluralizeToString('port', placesCount)}</div>
                  )}
                  {!!countriesCount && (
                    <div>{pluralizeToString('country', countriesCount)}</div>
                  )}
                  {cities && (
                    <ItineraryTooltip>
                      View itinerary
                      <AutoTooltipContent
                        width={250}
                        type="dark"
                        position="below"
                      >
                        <BodyText variant="small">
                          {[...new Set(cities)].join(' - ')}
                        </BodyText>
                      </AutoTooltipContent>
                    </ItineraryTooltip>
                  )}
                </DetailsCaption>

                <VerticalSpacer gap={12}>
                  <PromotionInclusionList
                      promotionDetails={promotionDetails}
                      evergreenInclusions={evergreenInclusions}
                    />
                  {!!inclusions?.length && !evergreenInclusions?.length &&
                    inclusions.map((inclusion) => {
                      return <PromotionInclusionItem
                        key={inclusion}
                        inclusion={inclusion}
                      />
                    })}

                  {description && !promotionDetails && (
                    <DestinationDescriptionText
                        type="compact"
                        fontSize="medium"
                        fontWeight="normal"
                        content={description}
                      />
                  )}
                  <DepartureDateWrapper>
                    <BodyText variant="medium" weight="bold" >
                      {earliestDepartureDate && `Departing on ${earliestDepartureDate}`}
                      {flashOffer && laterDates && !!laterDates?.length &&
                        <DepartureDate>
                          and more
                        </DepartureDate>}
                      {!flashOffer && laterDates && !!laterDates?.length &&
                        <DepartureDateTooltip>
                          and more
                          <AutoTooltipContent width={250} type="dark" position="above">
                            <BodyText variant="small">
                              {laterDates.join(', ')}
                            </BodyText>
                          </AutoTooltipContent>
                        </DepartureDateTooltip>
                        }
                    </BodyText>
                  </DepartureDateWrapper>
                </VerticalSpacer>
              </DescriptionSpace>
            </DescriptionSection>

            <PriceSection>
              <TripGuard>
                <CSSBreakpoint max="tablet">
                  <OffsetBookmarkContainer>
                    {bookmarkButton}
                  </OffsetBookmarkContainer>
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop">
                  <FloatingBookmarkContainer>
                    {bookmarkButton}
                  </FloatingBookmarkContainer>
                </CSSBreakpoint>
              </TripGuard>
              {cruiseLine.imageId && (
                <OperatorLogoArea>
                  <OperatorLogoImage
                    id={cruiseLine.imageId}
                    mobileWidth="75px"
                    tabletWidth="150px"
                    quality="eco"
                    alt={cruiseLine.name}
                    role="presentation"
                  />
                </OperatorLogoArea>
              )}
              {!pricingSection && (
                <PriceSummary>
                  <VerticalSpacer gap={8}>
                    {!disableDeposits && (
                      <div>
                        <BodyText variant="small">
                          {promotionDetails?.deposit && (
                            <Pill
                              kind="tertiary"
                              variant="primary"
                              startIcon={<LineClockDollarIcon />}
                            >
                              Secure with a{' '}
                              {promotionDetails.deposit.formattedValue} deposit
                            </Pill>
                          )}
                          {!promotionDetails?.deposit && (
                            <Pill
                              kind="tertiary"
                              variant="primary"
                              startIcon={<LineClockDollarIcon />}
                            >
                              Deposits available
                            </Pill>
                          )}
                        </BodyText>
                      </div>
                    )}

                    <PriceContainer>
                      <div>
                        {lowestOverallPriceDetails?.cabinCategory && (
                          <Caption variant="medium" colour="neutral-three">
                            {lowestOverallPriceDetails.cabinCategory} from
                          </Caption>
                        )}
                        {primaryPrice && (
                          <InlineGroup vertical="baseline">
                            <Heading variant="heading3" as="span">
                              <FormatCurrency
                                currency={primaryPrice.currencyCode}
                                value={primaryPrice.total ?? 0}
                              />
                            </Heading>
                            <BodyText
                              variant="medium"
                              weight="normal"
                              as="span"
                            >
                              /{primaryPrice.type}
                            </BodyText>
                          </InlineGroup>
                        )}

                        {!secondaryPrice &&
                          discountPills?.price &&
                          discountPills?.discountPercentage && (
                            <ValueWrapper variant="large" weight="bold">
                              <Caption
                                variant="medium"
                                weight="bold"
                                colour="neutral-one"
                              >
                                Valued up to{' '}
                                <FormatCurrency value={discountPills.price} />
                              </Caption>
                              <Pill kind="primary" variant="success">
                                -{discountPills.discountPercentage}%
                              </Pill>
                              <CruiseDiscountTermsAndConditions />
                            </ValueWrapper>
                        )}

                        {secondaryPrice && (
                          <>
                            <Caption
                              variant="large"
                              colour="neutral-three"
                              as="spam"
                            >
                              <FormatCurrency
                                currency={secondaryPrice.currencyCode}
                                value={secondaryPrice.total ?? 0}
                              />
                            </Caption>
                            <Caption
                              variant="large"
                              colour="neutral-three"
                              as="spam"
                            >
                              /{secondaryPrice.type} · Twin Share
                            </Caption>
                            {discountPills?.price &&
                              discountPills?.discountPercentage && (
                                <ValueWrapper variant="large" weight="bold">
                                  <Caption
                                    variant="medium"
                                    weight="bold"
                                    colour="neutral-one"
                                  >
                                    Valued up to{' '}
                                    <FormatCurrency
                                      value={discountPills.price}
                                    />
                                  </Caption>
                                  <Pill kind="primary" variant="success">
                                    -{discountPills.discountPercentage}%
                                  </Pill>
                                  <CruiseDiscountTermsAndConditions />
                                </ValueWrapper>
                            )}
                          </>
                        )}

                        <Caption variant="medium" colour="neutral-three">
                          Includes taxes & fees
                        </Caption>
                        {!secondaryPrice && (
                          <Caption variant="medium" colour="neutral-three">
                            {' '}
                            Twin Share{' '}
                          </Caption>
                        )}
                      </div>
                      <PriceSectionButton kind="primary" nonInteractable>
                        {isMobileScreen ? 'View' : 'View offer'}
                      </PriceSectionButton>
                    </PriceContainer>
                  </VerticalSpacer>
                </PriceSummary>
              )}
              {pricingSection}
            </PriceSection>
          </DescriptionBody>
        </Description>
      </PrimaryButtonPaletteProvider>
    </Cruise>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  isVelocityEnabled:
    state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
})

export default connect(mapStateToProps)(CruisesOfferTile)
